import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, IconButton, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import XtramModal, {
    XtramButton,
    XtramCheckbox,
    XtramField,
    XtramInput,
    XtramNotice,
    XtramSelect,
    xtramStyles,
} from '../../Widgets/XtramModal/XtramModal.jsx';
import EnumsService from '../../../Services/enums.service.js';
import { PERMANENT_FILE_TITLE_NAME, isPermanentFileName } from '../../../Utils/otherDocumentSections';
import styles from './OtherDocumentsXtram.module.scss';

const normalize = (value) => (value || '').trim().toLowerCase();

const readError = async (response) => {
    try {
        return await response.text();
    } catch {
        return '';
    }
};

/** Inline rename: Enter saves, Escape cancels. */
const RenameField = ({ direction, initial, onSave, onCancel, busy, t }) => {
    const [value, setValue] = useState(initial);
    return (
        <div className={styles.itemName}>
            <div className={styles.row}>
                <div className={styles.grow}>
                    <XtramInput
                        direction={direction}
                        autoFocus
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') onSave(value.trim());
                            if (e.key === 'Escape') onCancel();
                        }}
                    />
                </div>
                <Tooltip title={t('save')}>
                    <span>
                        <IconButton size="small" onClick={() => onSave(value.trim())} disabled={busy || !value.trim()}>
                            <CheckIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t('Cancel')}>
                    <IconButton size="small" onClick={onCancel}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    );
};

/**
 * Titles (OtherDocumentsTitle, shown as categories) of the accountant and the document types assigned to them.
 * Document types themselves are managed in OtherDocumentTypesModal. `onChanged()` reloads the page's types and titles.
 */
const OtherDocumentCategoriesModal = ({
    open,
    onClose,
    onChanged,
    t,
    direction,
    accountantId,
    token,
    types = [],
    titles = [],
}) => {
    const [busy, setBusy] = useState(false);
    const [newTitle, setNewTitle] = useState({ name: '', hidden: false });
    const [renaming, setRenaming] = useState(null);
    const [deleteTitle, setDeleteTitle] = useState(null);
    const [selectedTitleIds, setSelectedTitleIds] = useState([]);
    const [permanentHidden, setPermanentHidden] = useState(true);

    useEffect(() => {
        if (!open) return;
        setNewTitle({ name: '', hidden: false });
        setRenaming(null);
        setSelectedTitleIds([]);
    }, [open]);

    const titleById = useMemo(() => new Map(titles.map((title) => [title.id, title])), [titles]);
    const sortedTypes = useMemo(
        () => [...types].filter((type) => type?.id).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
        [types]
    );

    const run = async (request, successKey, errorKey) => {
        setBusy(true);
        try {
            const response = await request();
            if (response.ok) {
                if (successKey) toast.success(t(successKey));
                await onChanged?.();
                return response;
            }
            toast.error(`${t(errorKey)}: ${await readError(response)}`);
            return null;
        } catch (error) {
            console.error(error);
            toast.error(t(errorKey));
            return null;
        } finally {
            setBusy(false);
        }
    };

    const newTitleName = newTitle.name.trim();
    const newTitleTaken = !!newTitleName && titles.some((title) => normalize(title.name) === normalize(newTitleName));

    const addTitle = async () => {
        if (!newTitleName || newTitleTaken || busy) return;
        const ok = await run(
            () => EnumsService.AddOtherDocumentsTitle(accountantId, newTitleName, token, newTitle.hidden),
            'TitleAddedSuccessfully',
            'TitleAddError'
        );
        if (ok) setNewTitle({ name: '', hidden: false });
    };

    const renameTitle = async (title, name) => {
        if (!name || name === title.name) {
            setRenaming(null);
            return;
        }
        const ok = await run(
            () => EnumsService.UpdateOtherDocumentsTitle(accountantId, title.id, { name }, token),
            'TitleUpdatedSuccessfully',
            'TitleUpdateError'
        );
        if (ok) setRenaming(null);
    };

    const setTitlesHidden = (ids, hidden) => ids.length > 0 && run(
        () => EnumsService.SetOtherDocumentsTitlesHidden(accountantId, ids, hidden, token),
        hidden ? 'TitlesHiddenSuccessfully' : 'TitlesShownSuccessfully',
        'TitleUpdateError'
    );

    const confirmDeleteTitle = async () => {
        const id = deleteTitle?.id;
        if (!id) return;
        const ok = await run(() => EnumsService.DeleteOtherDocumentsTitle(id, token), 'TitleDeletedSuccessfully', 'TitleDeleteError');
        if (ok) {
            setDeleteTitle(null);
            setSelectedTitleIds((prev) => prev.filter((selected) => selected !== id));
        }
    };

    const setTitleTypes = async (title, nextOptions) => {
        const nextIds = new Set((nextOptions || []).map((option) => option.value));
        const currentIds = new Set(sortedTypes.filter((type) => type.titleId === title.id).map((type) => type.id));
        const added = [...nextIds].filter((id) => !currentIds.has(id));
        const removed = [...currentIds].filter((id) => !nextIds.has(id));
        if (added.length > 0) {
            const ok = await run(
                () => EnumsService.AssignTypesToOtherDocumentsTitle(accountantId, title.id, added, token),
                removed.length ? null : 'TypesAssignedSuccessfully',
                'TypesAssignError'
            );
            if (!ok) return;
        }
        if (removed.length > 0) {
            await run(
                () => EnumsService.AssignTypesToOtherDocumentsTitle(accountantId, null, removed, token),
                'TypesAssignedSuccessfully',
                'TypesAssignError'
            );
        }
    };

    const convertPermanentFile = () => run(
        () => EnumsService.EnsurePermanentFileTitle(accountantId, permanentHidden, token),
        'PermanentFileConvertedSuccessfully',
        'TitleAddError'
    );

    const typeOptionsFor = (title) => sortedTypes.map((type) => {
        const other = type.titleId && type.titleId !== title.id ? titleById.get(type.titleId) : null;
        return { value: type.id, label: other ? `${type.name} (${other.name})` : type.name };
    });
    const hasPermanentFileTitle = titles.some((title) => title.name === PERMANENT_FILE_TITLE_NAME);
    const builtInPermanentTypes = sortedTypes.filter((type) => !titleById.has(type.titleId) && isPermanentFileName(type.name));
    const allTitlesSelected = titles.length > 0 && selectedTitleIds.length === titles.length;

    return (
        <>
            <XtramModal
                open={open}
                onClose={onClose}
                title={t('ManageCategories')}
                direction={direction}
                wide
                busy={busy}
                footer={<XtramButton variant="secondary" onClick={onClose} disabled={busy}>{t('close')}</XtramButton>}
            >
                <div className={styles.stack}>
                    <div className={styles.addCard}>
                        <XtramField
                            label={t('AddTitle')}
                            className={styles.grow}
                            error={newTitleTaken ? t('OtherDocumentsTitleExists') : null}
                            hint={t('AddTitleDescription')}
                        >
                            <XtramInput
                                direction={direction}
                                value={newTitle.name}
                                onChange={(e) => setNewTitle((prev) => ({ ...prev, name: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') addTitle(); }}
                                placeholder={t('TitleName')}
                                error={newTitleTaken}
                            />
                        </XtramField>
                        <XtramCheckbox
                            checked={newTitle.hidden}
                            onChange={(hidden) => setNewTitle((prev) => ({ ...prev, hidden }))}
                            label={t('HideTitleFromClient')}
                        />
                        <XtramButton onClick={addTitle} disabled={!newTitleName || newTitleTaken || busy}>{t('Add')}</XtramButton>
                    </div>

                    {!hasPermanentFileTitle && (
                        <div className={styles.item}>
                            <div className={styles.itemHeader}>
                                <span className={styles.itemName}>
                                    {PERMANENT_FILE_TITLE_NAME}
                                    <span className={styles.builtInTag}>{t('BuiltInSection')}</span>
                                </span>
                                <XtramCheckbox checked={permanentHidden} onChange={setPermanentHidden} label={t('HideTitleFromClient')} />
                                <XtramButton small onClick={convertPermanentFile} disabled={busy}>{t('AddPermanentFileAsCategory')}</XtramButton>
                            </div>
                            <span className={styles.muted}>
                                {builtInPermanentTypes.length > 0 ? builtInPermanentTypes.map((type) => type.name).join(', ') : t('NoDocumentTypes')}
                            </span>
                        </div>
                    )}

                    {titles.length > 0 && (
                        <div className={xtramStyles.toolbar}>
                            <label className={xtramStyles.checkboxRow}>
                                <Checkbox
                                    size="small"
                                    checked={allTitlesSelected}
                                    indeterminate={selectedTitleIds.length > 0 && !allTitlesSelected}
                                    onChange={() => setSelectedTitleIds(allTitlesSelected ? [] : titles.map((title) => title.id))}
                                />
                                <span>{t('Select all')}</span>
                            </label>
                            <div className={xtramStyles.toolbarGroup}>
                                <XtramButton variant="ghost" small onClick={() => setTitlesHidden(selectedTitleIds, true)} disabled={!selectedTitleIds.length || busy}>
                                    {t('HideSelected')}
                                </XtramButton>
                                <XtramButton variant="ghost" small onClick={() => setTitlesHidden(selectedTitleIds, false)} disabled={!selectedTitleIds.length || busy}>
                                    {t('ShowSelected')}
                                </XtramButton>
                            </div>
                        </div>
                    )}

                    {titles.length === 0 ? (
                        <div className={xtramStyles.emptyState}>{t('NoTitlesYet')}</div>
                    ) : (
                        <div className={styles.list}>
                            {titles.map((title) => {
                                const options = typeOptionsFor(title);
                                const assignedIds = new Set(sortedTypes.filter((type) => type.titleId === title.id).map((type) => type.id));
                                const isRenaming = renaming === title.id;
                                return (
                                    <div key={title.id} className={`${styles.item} ${title.hiddenFromClient ? styles.hiddenItem : ''}`}>
                                        <div className={styles.itemHeader}>
                                            <Checkbox
                                                size="small"
                                                checked={selectedTitleIds.includes(title.id)}
                                                onChange={() => setSelectedTitleIds((prev) => (
                                                    prev.includes(title.id) ? prev.filter((id) => id !== title.id) : [...prev, title.id]
                                                ))}
                                            />
                                            {isRenaming ? (
                                                <RenameField
                                                    t={t}
                                                    direction={direction}
                                                    initial={title.name}
                                                    busy={busy}
                                                    onSave={(name) => renameTitle(title, name)}
                                                    onCancel={() => setRenaming(null)}
                                                />
                                            ) : (
                                                <span className={styles.itemName} title={title.name}>{title.name}</span>
                                            )}
                                            <span className={styles.countPill}>{assignedIds.size}</span>
                                            <XtramCheckbox
                                                checked={!!title.hiddenFromClient}
                                                onChange={(hidden) => setTitlesHidden([title.id], hidden)}
                                                label={t('HiddenFromClient')}
                                                disabled={busy}
                                            />
                                            <div className={styles.itemActions}>
                                                <Tooltip title={t('Edit')}>
                                                    <span>
                                                        <IconButton size="small" onClick={() => setRenaming(title.id)} disabled={busy}>
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title={t('Delete')}>
                                                    <span>
                                                        <IconButton size="small" onClick={() => setDeleteTitle(title)} disabled={busy}>
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        </div>
                                        <XtramField label={t('AssignedTypes')} size="full">
                                            <XtramSelect
                                                direction={direction}
                                                multiple
                                                options={options}
                                                value={options.filter((option) => assignedIds.has(option.value))}
                                                onChange={(next) => setTitleTypes(title, next)}
                                                placeholder={t('SelectDocumentTypes')}
                                                disabled={busy}
                                            />
                                        </XtramField>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </XtramModal>

            <XtramModal
                open={!!deleteTitle}
                onClose={() => setDeleteTitle(null)}
                title={t('OtherDocumentsDeleteTitle')}
                direction={direction}
                busy={busy}
                footer={(
                    <>
                        <XtramButton variant="secondary" onClick={() => setDeleteTitle(null)} disabled={busy}>{t('Cancel')}</XtramButton>
                        <XtramButton variant="danger" onClick={confirmDeleteTitle} disabled={busy} busy={busy}>{t('Delete')}</XtramButton>
                    </>
                )}
            >
                {deleteTitle && (
                    <XtramNotice type="warning">{t('OtherDocumentsDeleteTitleConfirm', { name: deleteTitle.name })}</XtramNotice>
                )}
            </XtramModal>
        </>
    );
};

export default OtherDocumentCategoriesModal;
